import { useState } from "react";
import { Box, TextField, Button, Stack, CircularProgress } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import { AuthLayout } from "./authLayout";
import { AuthHeader } from "./authHeader";
import { getPlatformFromUrl } from "../../utils/platform-mapping";
import { checkEmailExists } from "../../services/auth.service";
import toast from "react-hot-toast";

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "transparent",
    "& fieldset": {
      borderColor: "#5f6368",
    },
    "&:hover fieldset": {
      borderColor: "#5f6368",
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.primary.main,
    },
  },
  "& .MuiInputLabel-root": {
    color: "#bdc1c6",
    "&.Mui-focused": {
      color: theme.palette.primary.main,
    },
  },
  "& input": {
    fontSize: "1.1rem",
    padding: "16px 14px",
  },
}));

interface EmailEntryProps {
  onSubmit: (email: string, existingAccount: boolean) => void;
  onCreateAccount: () => void;
}

const MotionBox = motion(Box);
const MotionButton = motion(Button);
const MotionForm = motion.form;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.3,
      when: "beforeChildren",
    },
  },
};

const formItemVariants = {
  hidden: {
    opacity: 0,
    x: 50, // Start from right
  },
  visible: (i: number) => ({
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.5,
      delay: i * 0.1, // Stagger delay based on index
      ease: [0.4, 0, 0.2, 1], // Custom easing for smooth motion
    },
  }),
};

export const EmailEntry = ({ onSubmit, onCreateAccount }: EmailEntryProps) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const platform = getPlatformFromUrl();
  const theme = useTheme();
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email.trim()) {
      setError("Please enter your email");
      return;
    }

    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    try {
      setIsLoading(true);
      const existingAccount = await checkEmailExists(email);
      if (!existingAccount) {
        toast.error(
          "Couldnt find the email you entered. Please check the email address or register new account"
        );
        setError("There is no account associated with this email.");
        return;
      }
      onSubmit(email, existingAccount);
    } catch (err) {
      setError("An error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout>
      <MotionBox
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: { xs: 8, md: 16 },
          alignItems: { xs: "stretch", md: "center" },
          width: "100%",
          height: "100%",
          justifyContent: "center",
          minHeight: { xs: "300px", md: "340px" },
        }}
      >
        {/* Left Column - Header - No animations */}
        <Box
          sx={{
            flex: { md: "0 1 40%" },
            display: "flex",
            flexDirection: "column",
            alignItems: { xs: "center", md: "flex-start" },
            textAlign: { xs: "center", md: "left" },
          }}
        >
          <AuthHeader
            title="Sign in"
            subtitle={`to continue to ${platform.name}`}
          />
        </Box>

        {/* Right Column - Form */}
        <MotionForm
          onSubmit={handleSubmit}
          variants={containerVariants}
          style={{
            flex: "0 1 60%",
            display: "flex",
            flexDirection: "column",
            gap: "40px",
            paddingBottom: "60px", // Add padding to avoid overlap with footer/badge
          }}
        >
          <AnimatePresence mode="wait">
            <MotionBox key="email-field" custom={0} variants={formItemVariants}>
              <StyledTextField
                fullWidth
                label="Email"
                variant="outlined"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError("");
                }}
                error={!!error}
                helperText={error}
                required
                autoComplete="email"
                inputProps={{
                  autoCapitalize: "none",
                  autoCorrect: "off",
                  spellCheck: "false",
                  dir: "ltr",
                }}
                disabled={isLoading}
              />
            </MotionBox>

            <MotionBox
              key="button-group"
              custom={1}
              variants={formItemVariants}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={3}
                justifyContent="flex-end"
                sx={{ mt: 2 }}
              >
                <MotionButton
                  key="create-account-button"
                  onClick={onCreateAccount}
                  size="large"
                  disabled={isLoading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  sx={{
                    color: "#2563EB",
                    fontSize: "0.95rem",
                    py: 1.5,
                    px: 3,
                    minWidth: { xs: "100%", sm: "auto" }, // Full width on mobile
                    "&:hover": {
                      backgroundColor: "rgba(37, 99, 235, 0.08)",
                    },
                    "&:disabled": {
                      color: "rgba(37, 99, 235, 0.5)",
                    },
                  }}
                >
                  Create account
                </MotionButton>
                <MotionButton
                  key="next-button"
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isLoading}
                  loading={isLoading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  sx={{
                    bgcolor: "#2563EB",
                    color: "#FFFFFF",
                    fontSize: "0.95rem",
                    py: 1.5,
                    px: 5,
                    minWidth: { xs: "100%", sm: "auto" }, // Full width on mobile
                    "&:hover": {
                      bgcolor: "#1D4ED8",
                    },
                    "&:disabled": {
                      bgcolor: "rgba(37, 99, 235, 0.5)",
                    },
                  }}
                >
                  {isLoading ? (
                    <CircularProgress size={24} sx={{ color: "#FFFFFF" }} />
                  ) : (
                    "Next"
                  )}
                </MotionButton>
              </Stack>
            </MotionBox>
          </AnimatePresence>
        </MotionForm>
      </MotionBox>
    </AuthLayout>
  );
};
